import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { getCommittedAttributionType } from '../../MultiFlightMap.helpers';
import { ActionOptions } from '../ActionOptions/ActionOptions';
import { AttributionRadiusPrompt } from '../AttributionPOI/AttributionRadiusPrompt/AttributionRadiusPrompt';
import { useMapPopover } from '../MapPopover/MapPopover.context';
import { VenueNamePopOver } from '../Venues/VenuesPopOver/VenueNamePopOver/VenueNamePopOver';
import { PopOverOptionsStyled } from './PopOverOptions.styled';

type Props = {
  onClearPositions?: () => void;
  optionsType: 'screens' | 'venues';
  position?: { latitude: number; longitude: number } | null;
};

type State = {
  showLocationRadiusModal: boolean;
};

export function PopOverOptions({ onClearPositions, optionsType, position }: Props) {
  const { formatMessage } = useIntl();
  const { addLocationCircle, addScreen, cancelTemporaryScreen, temporaryScreen, temporaryVenues, type } =
    useMultiFlightMap();
  const canAddVenue = optionsType === 'screens' || getCommittedAttributionType(temporaryVenues) !== 'driveToStore';
  const { closeMapPopover } = useMapPopover();
  const [{ showLocationRadiusModal, showVenuePopOver }, setState] = useState({
    showLocationRadiusModal: false,
    showVenuePopOver: false
  });

  const handleShowModal = (show: boolean, modal: keyof State) => {
    setState(prevState => ({
      ...prevState,
      [modal]: show
    }));
  };

  const handleLocationClick = async () => handleShowModal(true, 'showLocationRadiusModal');

  const handleAddLocation = async (radius: number) => {
    if (position) {
      await addLocationCircle(position.latitude, position.longitude, radius);
      onClearPositions?.();
      closeMapPopover();
    }

    setState(prevState => ({
      ...prevState,
      showLocationRadiusModal: false
    }));
  };

  const handleVenueClick = () => {
    setState(prevState => ({
      ...prevState,
      showVenuePopOver: true
    }));
    if (temporaryScreen) {
      addScreen(temporaryScreen);
      closeMapPopover();
    }
  };

  const handleCancelClick = () => {
    if (optionsType === 'screens') {
      cancelTemporaryScreen();
    }
    closeMapPopover();
    onClearPositions?.();
  };

  return (
    <>
      <PopOverOptionsStyled className="venuesPopOverOptions">
        {showVenuePopOver ? (
          <VenueNamePopOver onClearPositions={onClearPositions} position={position} />
        ) : (
          <ActionOptions
            className="venuesPopOverOptions__option"
            options={[
              {
                icon: 'fa-solid fa-plus',
                id: 'AddLocationOption',
                onClick: handleLocationClick,
                text: formatMessage({
                  defaultMessage: 'Location',
                  id: 'multiFlightVenues.location'
                }),
                visibility: type !== 'dooh'
              },
              {
                icon: 'fa-solid fa-plus',
                id: `${optionsType === 'screens' ? 'AddScreen' : 'AddAsVenue'}Option`,
                onClick: handleVenueClick,
                text: formatMessage({
                  defaultMessage: optionsType === 'screens' ? 'Add Screen' : 'Add as Venue',
                  id: `multiFlightVenues.${optionsType === 'screens' ? 'screens' : 'venues'}`
                }),
                visibility: canAddVenue
              },
              {
                icon: 'fa-solid fa-xmark',
                id: 'CancelOption',
                onClick: handleCancelClick,
                text: formatMessage({
                  defaultMessage: 'Cancel',
                  id: 'common.cancel'
                }),
                visibility: true
              }
            ].filter(f => f.visibility)}
          />
        )}
      </PopOverOptionsStyled>
      {showLocationRadiusModal && (
        <AttributionRadiusPrompt
          onCancel={() => handleShowModal(false, 'showLocationRadiusModal')}
          onConfirm={meters => handleAddLocation(meters / 1000)}
        />
      )}
    </>
  );
}
